import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState, useEffect } from 'react';
import { Splide } from '@splidejs/react-splide';
import { Box, IconWrapper, Icon, Optional } from '@honda-canada/design-system-react';
import keypressCallback from '@honda-canada/js-utilities/lib/keypressCallback';
import { useTranslation } from 'react-i18next';
import { getIsDarkMode } from '../../utils/sitecoreFields';
import themeStyles from './MinimalCarousel.styles';
import useCarouselEffects from '../../utils/hooks/useCarouselEffects';
import { useRoute } from '../../utils/sitecoreContext';
const CarouselContainer = themeStyles.apply(Box, 'CarouselContainer');
const PrevIconWrapper = themeStyles.apply(IconWrapper, 'PrevIconWrapper');
const NextIconWrapper = themeStyles.apply(IconWrapper, 'NextIconWrapper');
const CarouselSlider = ({ index, length, children, arrowColor = 'grey.0', paginationBgColor = 'white', splideOptions, carouselStyles, showArrows = true, targetSlideIndex, onSliderArrowClick, }) => {
    var _a;
    const carouselEl = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isLastSlideVisible, setIsLastSlideVisible] = useState(false);
    const { t } = useTranslation();
    const route = useRoute();
    const enterKeyCode = 13;
    const setCurrentSlidePage = (slideIndex) => {
        var _a, _b;
        (_b = (_a = carouselEl === null || carouselEl === void 0 ? void 0 : carouselEl.current) === null || _a === void 0 ? void 0 : _a.splide) === null || _b === void 0 ? void 0 : _b.go(slideIndex);
    };
    const isDark = getIsDarkMode(route);
    const containerBackgroundColor = isDark ? 'black' : paginationBgColor;
    const chevronIconColor = isDark ? 'grey.2' : arrowColor;
    const perMove = (_a = splideOptions === null || splideOptions === void 0 ? void 0 : splideOptions.perMove) !== null && _a !== void 0 ? _a : 1;
    useCarouselEffects({ carouselEl, index, perMove, length, setCurrentSlide });
    const options = {
        pagination: false,
        arrows: false,
        rewind: false,
        perPage: 1,
        padding: {
            left: '20px',
            right: '20vw',
        },
        perMove,
        gap: '24px',
        fixedWidth: '294px',
        ...splideOptions,
    };
    useEffect(() => {
        const { splide } = (carouselEl === null || carouselEl === void 0 ? void 0 : carouselEl.current) || {};
        const checkLastSlideVisibility = () => {
            var _a;
            const slidesArray = carouselEl.current.slides;
            const lastSlideElement = slidesArray[slidesArray.length - 1];
            setIsLastSlideVisible((_a = lastSlideElement === null || lastSlideElement === void 0 ? void 0 : lastSlideElement.classList) === null || _a === void 0 ? void 0 : _a.contains('is-visible'));
        };
        checkLastSlideVisibility();
        splide === null || splide === void 0 ? void 0 : splide.on('moved', checkLastSlideVisibility);
        return () => {
            splide === null || splide === void 0 ? void 0 : splide.off('moved', checkLastSlideVisibility);
        };
    }, [length]);
    // This useEffect hook updates the slide transition speed of the carousel to enable an instant transition to
    // a target slide index. It temporarily sets the speed to 0, transitions to the specified slide,
    // then restores the original speed after a brief pause.
    useEffect(() => {
        var _a;
        const { splide } = (carouselEl === null || carouselEl === void 0 ? void 0 : carouselEl.current) || {};
        if (splide && targetSlideIndex) {
            const originalSpeed = (_a = splide.options) === null || _a === void 0 ? void 0 : _a.speed;
            splide.options.speed = 0;
            const slideIndex = length && length - 1 === targetSlideIndex ? targetSlideIndex - 1 : targetSlideIndex;
            splide === null || splide === void 0 ? void 0 : splide.go(slideIndex);
            const timer = setTimeout(() => {
                splide.options.speed = originalSpeed;
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [targetSlideIndex]);
    function handleActionOnSliderArrowClick() {
        if (onSliderArrowClick)
            onSliderArrowClick();
    }
    return (_jsxs(CarouselContainer, { carouselStyles: carouselStyles, "data-testid": "cy-card-grid-carousel", children: [_jsx(Optional, { when: length > 1 && showArrows, children: _jsx(PrevIconWrapper, { onClick: (e) => {
                        if (currentSlide === 0) {
                            e.preventDefault();
                        }
                        else {
                            carouselEl.current.splide.go('<');
                            handleActionOnSliderArrowClick();
                        }
                    }, onKeyDown: keypressCallback(enterKeyCode, () => {
                        if (currentSlide !== 0) {
                            carouselEl.current.splide.go('<');
                            handleActionOnSliderArrowClick();
                        }
                    }), opacity: currentSlide === 0 ? 0.5 : 1, role: "button", tabIndex: 0, cursor: "pointer", "aria-label": t('Shared.Common.previousSlideAria'), display: ['flex'], "data-testid": "arrow-left", backgroundColor: containerBackgroundColor, children: _jsx(Icon, { name: "arrowLeft", iconSize: "large", height: "13px", iconColor: chevronIconColor }) }) }), _jsx(Splide, { options: options, ref: carouselEl, onMove: (_, slideIndex) => {
                    setCurrentSlide(slideIndex);
                }, children: children({ currentSlide, setCurrentSlidePage }) }), _jsx(Optional, { when: length > 1 && showArrows, children: _jsx(NextIconWrapper, { onClick: (e) => {
                        if (isLastSlideVisible) {
                            e.preventDefault();
                        }
                        else {
                            carouselEl.current.splide.go('>');
                            handleActionOnSliderArrowClick();
                        }
                    }, onKeyDown: keypressCallback(enterKeyCode, () => {
                        if (!isLastSlideVisible) {
                            carouselEl.current.splide.go('>');
                            handleActionOnSliderArrowClick();
                        }
                    }), opacity: isLastSlideVisible ? 0.5 : 1, role: "button", tabIndex: 0, cursor: "pointer", "aria-label": t('Shared.Common.nextSlideAria'), display: "flex", "data-testid": "arrow-right", backgroundColor: containerBackgroundColor, children: _jsx(Icon, { name: "arrowRight", iconSize: "large", height: "13px", iconColor: chevronIconColor }) }) })] }));
};
export default CarouselSlider;
